import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="renewal-selection-modal"
export default class extends Controller {
  static targets = ["modal", "searchInput", "renewalsList", "selectedRenewal", "addButton"]
  static values = {
    workspaceId: String,
    selectedClaimsExperienceIds: Array,
    selectedType: String
  }

  connect() {
    this.currentPage = 1
    this.currentSearch = ""
    this.selectedRenewalId = null

    // Listen for modal open events
    this.boundHandleModalOpen = this.handleModalOpen.bind(this)
    document.addEventListener('open-renewal-modal', this.boundHandleModalOpen)
  }

  disconnect() {
    document.removeEventListener('open-renewal-modal', this.boundHandleModalOpen)
  }

  handleModalOpen(event) {
    this.open(event.detail.selectedIds, event.detail.type)
  }

  // Open the modal and load renewals
  open(selectedIds = [], type = "claims_experience") {
    this.selectedClaimsExperienceIdsValue = selectedIds
    this.selectedTypeValue = type
    this.showModal()
    this.loadRenewals()
  }

  // Close the modal
  close() {
    this.hideModal()
    this.resetModal()
  }

  // Handle search input
  search(event) {
    event.preventDefault()
    this.currentSearch = this.searchInputTarget.value.trim()
    this.currentPage = 1
    this.loadRenewals()
  }

  // Handle pagination
  goToPage(event) {
    event.preventDefault()
    this.currentPage = parseInt(event.target.dataset.page)
    this.loadRenewals()
  }

  // Handle renewal selection
  selectRenewal(event) {
    // Find the renewal row element (may be clicked element or its parent)
    const renewalRow = event.target.closest("[data-renewal-id]")
    const renewalId = renewalRow.dataset.renewalId
    const renewalName = renewalRow.dataset.renewalName

    // Remove previous selection styling
    this.renewalsListTarget.querySelectorAll("[data-renewal-id]").forEach(el => {
      el.classList.remove("bg-blue-50", "border-blue-300")
      el.classList.add("hover:bg-gray-50")
    })

    // Add selection styling to clicked renewal
    renewalRow.classList.add("bg-blue-50", "border-blue-300")
    renewalRow.classList.remove("hover:bg-gray-50")

    this.selectedRenewalId = renewalId
    this.selectedRenewalTarget.textContent = `Selected: ${renewalName}`
    this.addButtonTarget.disabled = false
    this.addButtonTarget.classList.remove("opacity-50", "cursor-not-allowed")
  }

  // Handle adding claims experiences/breakdowns to renewal
  async addToRenewal() {
    if (!this.selectedRenewalId || this.selectedClaimsExperienceIdsValue.length === 0) {
      return
    }

    this.addButtonTarget.disabled = true
    this.addButtonTarget.textContent = "Adding..."

    try {
      let endpoint, bodyData, successMessage, itemType

      if (this.selectedTypeValue === "claims_experience_breakdown") {
        endpoint = `/workspaces/${this.workspaceIdValue}/renewals/${this.selectedRenewalId}/add_claims_experience_breakdowns`
        bodyData = { claims_experience_breakdown_ids: this.selectedClaimsExperienceIdsValue }
        itemType = "claims experience breakdowns"
      } else {
        endpoint = `/workspaces/${this.workspaceIdValue}/renewals/${this.selectedRenewalId}/add_claims_experiences`
        bodyData = { claims_experience_ids: this.selectedClaimsExperienceIdsValue }
        itemType = "claims experiences"
      }

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').getAttribute("content")
        },
        body: JSON.stringify(bodyData)
      })

      if (response.ok) {
        const data = await response.json()
        this.showSuccessMessage(`Successfully added ${this.selectedClaimsExperienceIdsValue.length} ${itemType} to renewal`)
        this.close()

        // Clear selections in parent controller
        if (this.selectedTypeValue === "claims_experience_breakdown" && window.claimsExperienceBreakdownSelector) {
          window.claimsExperienceBreakdownSelector.clearAll()
        } else if (window.claimsExperienceSelector) {
          window.claimsExperienceSelector.clearAll()
        }

        // Refresh page to show updated data
        window.location.reload()
      } else {
        const errorData = await response.json()
        this.showErrorMessage(errorData.error || `Failed to add ${itemType} to renewal`)
      }
    } catch (error) {
      console.error("Error adding to renewal:", error)
      this.showErrorMessage("An error occurred while adding to renewal")
    }

    this.addButtonTarget.disabled = false
    this.addButtonTarget.textContent = "Add to Renewal"
  }

  // Load renewals via AJAX
  async loadRenewals() {
    try {
      const params = new URLSearchParams({
        page: this.currentPage,
        search: this.currentSearch
      })

      const response = await fetch(`/workspaces/${this.workspaceIdValue}/renewals.json?${params}`)
      const data = await response.json()

      this.renderRenewals(data)
    } catch (error) {
      console.error("Error loading renewals:", error)
      this.renewalsListTarget.innerHTML = '<p class="text-red-600 p-4">Error loading renewals</p>'
    }
  }

  // Render renewals in the modal
  renderRenewals(data) {
    if (!data.renewals || data.renewals.length === 0) {
      this.renewalsListTarget.innerHTML = `
        <div class="p-4 text-center text-gray-500">
          ${this.currentSearch ? `No renewals found for "${this.currentSearch}"` : 'No renewals available'}
        </div>
      `
      return
    }

    let html = '<div class="divide-y divide-gray-200">'

    data.renewals.forEach(renewal => {
      html += `
        <div class="p-4 cursor-pointer hover:bg-gray-50 border border-transparent"
             data-renewal-id="${renewal.id}"
             data-renewal-name="${renewal.name || renewal.id}"
             data-action="click->renewal-selection-modal#selectRenewal">
          <div class="flex items-center justify-between">
            <div>
              <h4 class="font-medium text-gray-900">${renewal.name || renewal.id}</h4>
              <p class="text-sm text-gray-500">
                Client: ${renewal.client_name || 'None'} •
                Created: ${new Date(renewal.created_at).toLocaleDateString()}
              </p>
            </div>
            <div class="text-sm text-gray-400">
              ${renewal.claims_experiences_count || 0} claims experiences
            </div>
          </div>
        </div>
      `
    })

    html += '</div>'

    // Add pagination if needed
    if (data.total_pages > 1) {
      html += this.renderPagination(data)
    }

    this.renewalsListTarget.innerHTML = html
  }

  // Render pagination controls
  renderPagination(data) {
    let paginationHtml = `
      <div class="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
        <div class="flex items-center justify-between">
          <div class="text-sm text-gray-700">
            Showing ${((data.current_page - 1) * 10) + 1} to ${Math.min(data.current_page * 10, data.total_count)} of ${data.total_count} results
          </div>
          <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
    `

    if (data.current_page > 1) {
      paginationHtml += `
        <button data-page="${data.current_page - 1}"
                data-action="click->renewal-selection-modal#goToPage"
                class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
          <span class="sr-only">Previous</span>
          <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd" />
          </svg>
        </button>
      `
    }

    // Show page numbers (simplified - just show current page context)
    for (let i = Math.max(1, data.current_page - 2); i <= Math.min(data.total_pages, data.current_page + 2); i++) {
      const isFirst = i === Math.max(1, data.current_page - 2) && data.current_page <= 1
      const isLast = i === Math.min(data.total_pages, data.current_page + 2) && data.current_page >= data.total_pages
      const roundingClasses = (isFirst ? ' rounded-l-md' : '') + (isLast ? ' rounded-r-md' : '')
      const activeClass = i === data.current_page ? 'bg-blue-50 text-blue-600 border-blue-300' : 'bg-white text-gray-700 hover:bg-gray-50'

      paginationHtml += `
        <button data-page="${i}"
                data-action="click->renewal-selection-modal#goToPage"
                class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${activeClass}${roundingClasses}">
          ${i}
        </button>
      `
    }

    if (data.current_page < data.total_pages) {
      paginationHtml += `
        <button data-page="${data.current_page + 1}"
                data-action="click->renewal-selection-modal#goToPage"
                class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
          <span class="sr-only">Next</span>
          <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
          </svg>
        </button>
      `
    }

    paginationHtml += '</nav></div></div>'
    return paginationHtml
  }

  // Modal utility methods
  showModal() {
    this.modalTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
  }

  hideModal() {
    this.modalTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
  }

  resetModal() {
    this.currentPage = 1
    this.currentSearch = ""
    this.selectedRenewalId = null
    this.searchInputTarget.value = ""
    this.selectedRenewalTarget.textContent = ""
    this.addButtonTarget.disabled = true
    this.addButtonTarget.classList.add("opacity-50", "cursor-not-allowed")
    this.renewalsListTarget.innerHTML = ""
  }

  showSuccessMessage(message) {
    // You can integrate with your flash message system here
    alert(message) // Temporary - replace with your flash message system
  }

  showErrorMessage(message) {
    // You can integrate with your flash message system here
    alert(message) // Temporary - replace with your flash message system
  }
}
